import { LitElement, html, css } from 'lit';
import { property, customElement } from 'lit/decorators.js';

import { authenticatedAPICall } from '../../client/api-client';
import '../../elements/pickup-order/pickup-order';
import '../../elements/search-dropdown/search-dropdown';
import { SearchDropdown } from '../../elements/search-dropdown/search-dropdown';

import { Product } from '../../types/types';
import { ProductionUnit, ProductionUnitSetting } from '../../types/types';
import { showNotification } from '../../LanghoutBetonApp';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

@customElement('langhout-beton-production-unit-settings')
export class LanghoutBetonProductionUnitSettings extends LitElement {
  static styles = css`
    :host([disableButtons]) {
      pointer-events: none;
      opacity: 0.7;
    }

    .wrapper {
      padding-bottom: 260px;
    }

    .row {
      width: 100%;
      margin-bottom: 30px;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
      flex-flow: row wrap;
    }

    .column {
      padding-left: 10px;
      padding-right: 10px;
      display: flex;
      flex-direction: column;
    }

    langhout-button {
      margin-left: 20px;
      min-width: 150px;
    }

    .langhout-button-label {
      height: 27px;
    }

    .label {
      text-align: left;
      color: var(--primary-color);
    }

    input {
      padding: 7px;
      width: calc(100% - 18px);
      border-radius: 5px;
      font-size: 16px;
    }

    search-dropdown {
      max-width: calc(100vw - 50px);
      width: 450px;
    }
  `;

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  @property({ type: Array })
  private productionUnits: ProductionUnit[] = [];

  constructor() {
    super();
    this.listProductionUnits();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  render() {
    return html`
      <div class="wrapper">
        <h2>Machines instellen</h2>
        <div class="column">
          ${this.productionUnits.map(productionUnit => {
            return html`
              <div class="colum">
                <h3>Machine ${productionUnit.name}</h3>
                <div class="row">
                  <div class="column">
                    <div class="label">Product:</div>
                    <search-dropdown
                      class="search-dropdown"
                      id="search-dropdown-${productionUnit.id}"
                      .selected=${this.getProduct(productionUnit)}
                      @change="${(e: CustomEvent) => {
                        const selectedProduct = JSON.parse(e.detail.value);
                        this.getProductionUnitSettings(
                          productionUnit.id,
                          selectedProduct.code,
                        );
                      }}"
                    ></search-dropdown>
                  </div>
                  <div class="column">
                    <div class="label">Stuks per kar:</div>
                    <input
                      type="number"
                      inputmode="numeric"
                      value="${productionUnit.setting.amount}"
                      min="0"
                      pattern="[0-9]*"
                      id="product-amount-${productionUnit.id}"
                      name="product-amount"
                      }}
                    />
                  </div>
                  <div class="column">
                    <div class="label langhout-button-label"></div>
                    <langhout-button
                      text="Wijzigen"
                      @click="${async () => {
                        this.disableButtons = true;
                        await this.createOrUpdateProductionUnitSettings(
                          productionUnit,
                        );
                        this.disableButtons = false;
                      }}"
                    ></langhout-button>
                  </div>
                </div>
              </div>
            `;
          })}
          <div class="row">
            <langhout-button
              text="Karren lossen"
              @click="${(): void => {
                window.location.href = '/stock-count';
              }}"
            ></langhout-button>
          </div>
        </div>
      </div>
    `;
  }

  getProduct = (productionUnit: ProductionUnit): Product | null => {
    if (productionUnit.setting.id === '00000000-0000-0000-0000-000000000000') {
      return null;
    }

    return <Product>{
      code: productionUnit.setting.product_code,
      description: productionUnit.setting.product_description,
    };
  };

  listProductionUnits = async () => {
    const path = `/production-unit`;

    const result = await authenticatedAPICall(path, 'GET', new Headers());
    if (result.statusCode !== undefined && result.statusCode >= 200 && result.statusCode < 300 && Array.isArray(result.body)) {
      this.productionUnits = result.body as ProductionUnit[];
    }
  };

  getProductionUnitSettings = async (
    productionUnitID: string,
    productCode: string,
  ) => {
    const path = `/production-unit/setting?production_unit_id=${productionUnitID}&product_code=${productCode}`;

    const result = await authenticatedAPICall(path, 'GET', new Headers());

    const body = result.body;
    if (!isRecord(body)) {
      return;
    }
    if (body.id != '00000000-0000-0000-0000-000000000000') {
      const temp = this.productionUnits;
      this.productionUnits = [];
      for (let i = 0; i < temp.length; i++) {
        if (temp[i].id === productionUnitID) {
          temp[i].setting = body as ProductionUnitSetting;
        }
      }
      this.productionUnits = temp;
    }
  };

  createOrUpdateProductionUnitSettings = async (
    productionUnit: ProductionUnit,
  ) => {
    if (!this.shadowRoot) {
      showNotification('something unexpected went wrong, please try again');
      return;
    }
    const amountInput = <HTMLInputElement>(
      this.shadowRoot.querySelector(`#product-amount-${productionUnit.id}`)
    );
    const amount = parseInt(amountInput.value);
    if (isNaN(amount) || amount === 0) {
      showNotification(
        `Stuks per kar moet groter zijn dan 0 voor ${productionUnit.name}`,
      );
      return;
    }

    const searchDropdown = <SearchDropdown>(
      this.shadowRoot.querySelector(`#search-dropdown-${productionUnit.id}`)
    );
    if (!searchDropdown.selected || searchDropdown.selected.code === '') {
      showNotification(
        `Geen product geselecteerd voor machine ${productionUnit.name}`,
      );
      return;
    }
    const path = `/production-unit/setting`;

    const result = await authenticatedAPICall(
      path,
      'POST',
      new Headers(),
      JSON.stringify({
        production_unit_id: productionUnit.id,
        amount: amount,
        product_code: searchDropdown.selected.code,
      }),
    );
    if (result.statusCode !== undefined && result.statusCode >= 200 && result.statusCode < 300) {
      showNotification(`Machine ${productionUnit.name} succesvol aangepast`);
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-production-unit-settings': LanghoutBetonProductionUnitSettings;
  }
}
